import clsx from 'clsx';
import { CalendarClock, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useActions, useMeetings, useMoneyUnavailable, useProjects, useTasks, useThreads, useWorkspace } from '@/api';
import type { Project } from '@/api/types';
import { ActionList } from '@/components/ActionList';
import { QueryPending } from '@/components/QueryState';
import { Section, Status } from '@/components/ui';
import { formatDate, formatDateFull, formatTime, formatWhen, plural } from '@/lib/format';
import { health } from '@/lib/status';

function ProjectRow({ p, waiting, unread }: { p: Project; waiting: number; unread: number }) {
  const h = health[p.health];
  return (
    <li>
      <Link to={`/p/${p.id}`} className="group flex items-center gap-4 py-3 hover:bg-panel">
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-x-3">
            <span className="font-medium">{p.title}</span>
            <Status tone={h.tone}>{h.label}</Status>
          </span>
          <span className="mt-0.5 block truncate text-sm text-muted">{p.now}</span>
        </span>
        {p.kind === 'fixed' ? (
          <span className="hidden w-40 md:block">
            <span className="flex gap-0.5" aria-hidden>
              {p.milestones.map((m) => (
                <span key={m.id} className={clsx('h-1 flex-1 rounded-full', m.state === 'done' ? 'bg-ink' : m.state === 'current' ? 'bg-ink/45' : 'bg-strong')} />
              ))}
            </span>
            <span className="mt-1 block text-xs text-muted">{p.milestones.find((m) => m.state === 'current')?.title}</span>
          </span>
        ) : (
          <span className="hidden w-40 text-xs text-muted md:block">
            {p.metrics[0]?.label}: <span className="num font-semibold text-ink">{p.metrics[0]?.value}</span> {p.metrics[0]?.change}
          </span>
        )}
        <span className="w-20 text-right text-xs">
          {waiting > 0 && <span className="block text-waiting">ждём: {waiting}</span>}
          {unread > 0 && <span className="block font-medium">новых: {unread}</span>}
        </span>
        <ChevronRight className="size-4 text-faint" />
      </Link>
    </li>
  );
}

export function Overview() {
  const { data: ws } = useWorkspace();
  const actionsQ = useActions();
  const actions = actionsQ.data;
  const moneyDown = useMoneyUnavailable();
  const projectsQ = useProjects();
  const projects = projectsQ.data;
  const { data: tasks } = useTasks();
  const { data: threads } = useThreads();
  const { data: meetings } = useMeetings();
  const n = actions?.length ?? 0;
  const projectTitle = (id?: string) => projects?.find((p) => p.id === id)?.title;
  const upcoming = meetings?.filter((m) => !m.outcome && m.status !== 'canceled' && new Date(m.at).getTime() > Date.now()).slice(0, 2) ?? [];

  // One two-column grid for the whole page: work on the left, context on the right,
  // so every block starts on the same vertical lines.
  return (
    <div className="grid grid-cols-1 gap-x-12 gap-y-10 lg:grid-cols-[minmax(0,1fr)_320px]">
      <div className="min-w-0 space-y-12">
        <header>
          <p className="text-sm text-muted">
            {ws ? `Добрый день, ${ws.me.name.split(' ')[0]}` : ' '}. Сегодня {formatDateFull(new Date().toISOString()).replace(/ г\.$/, '')}.
          </p>
          <h1 className="mt-1 text-xl font-semibold tracking-tight">{!actions ? ' ' : n === 0 ? (moneyDown ? 'Список дел может быть неполным' : 'От вас сейчас ничего не нужно') : `Ждут вас: ${n} ${plural(n, 'дело', 'дела', 'дел')}`}</h1>
          {n > 0 && <p className="mt-1 text-sm text-muted">Каждый ответ снимает команду с паузы. Начните с «Решить».</p>}
          <div className="mt-6" data-tour="waiting">
            {moneyDown && (
              <p role="alert" className="mb-4 rounded border border-line px-3 py-2 text-sm">
                Счета сейчас недоступны: сервис счетов не ответил. Счета к оплате в этом списке не показаны, обновите страницу чуть позже.
              </p>
            )}
            {!actions ? <QueryPending query={actionsQ} compact /> : n === 0 && moneyDown ? null : n === 0 ? <p className="text-muted">Команда работает, новости появятся в проектах.</p> : <ActionList actions={actions} projectTitle={projectTitle} columns={1} />}
          </div>
        </header>

        <Section title="Проекты">
          {!projects ? (
            <QueryPending query={projectsQ} compact />
          ) : (
            <ul className="divide-y divide-line border-y border-line">
              {projects.map((p) => (
                <ProjectRow
                  key={p.id}
                  p={p}
                  waiting={tasks?.filter((t) => t.projectId === p.id && t.needsClient && t.status !== 'done').length ?? 0}
                  unread={threads?.filter((t) => t.projectId === p.id).reduce((s, t) => s + t.unread, 0) ?? 0}
                />
              ))}
            </ul>
          )}
        </Section>
      </div>

      <aside className="space-y-10 lg:sticky lg:top-[80px] lg:self-start">
          <Section title="Ближайшие встречи">
            {upcoming.length === 0 ? (
              <p className="text-sm text-muted">Встреч не запланировано.</p>
            ) : (
              <ul className="space-y-3">
                {upcoming.map((m) => (
                  <li key={m.id}>
                    <Link to={`/p/${m.projectId}/meetings`} className="flex gap-3 hover:underline">
                      <CalendarClock className="mt-0.5 size-4 shrink-0 text-muted" />
                      <span>
                        <span className="block text-sm font-medium">{m.title}</span>
                        <span className="block text-xs text-muted">
                          {formatDate(m.at)}, {formatTime(m.at)}, {projectTitle(m.projectId)}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Section>
          <Section title="Свежие сообщения" aside={<Link to="/inbox" className="hover:text-ink">все</Link>}>
            <ul className="space-y-3">
              {threads?.slice(0, 4).map((t) => (
                <li key={t.id}>
                  <Link to={`/p/${t.projectId}/talk/${t.id}`} className="block hover:underline">
                    <span className="flex items-baseline gap-2">
                      <span className="min-w-0 flex-1 truncate text-sm font-medium">{t.title}</span>
                      {t.unread > 0 && <span className="num rounded-full bg-ink px-1.5 text-[10px] font-semibold leading-4 text-inverse">{t.unread}</span>}
                    </span>
                    <span className="block truncate text-xs text-muted">
                      {t.lastAuthor?.side === 'client' ? 'Вы' : t.lastAuthor?.name.split(' ')[0]}: {t.lastMessage}, {formatWhen(t.lastMessageAt)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </Section>
      </aside>
    </div>
  );
}
